import { isHexString } from 'ethers'
import { FC, memo, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NativeScrollEvent, ScrollView, View } from 'react-native'

import { Hex } from '@ambire-common/interfaces/hex'
import { ISignMessageController } from '@ambire-common/interfaces/signMessage'
import { isValidAddress } from '@ambire-common/services/address'
import WarningFilledIcon from '@common/assets/svg/WarningFilledIcon'
import HumanizerAddress from '@common/components/HumanizerAddress'
import MultistateToggleButton from '@common/components/MultistateToggleButton'
import Text from '@common/components/Text'
import { i18nKey } from '@common/config/localization/constants'
import useTheme from '@common/hooks/useTheme'
import useWindowSize from '@common/hooks/useWindowSize'
import spacings from '@common/styles/spacings'
import flexbox from '@common/styles/utils/flexbox'
import { getMessageAsText, simplifyTypedMessage } from '@common/utils/messageToString'

import getStyles from './styles'

const isCloseToBottom = ({ layoutMeasurement, contentOffset, contentSize }: NativeScrollEvent) => {
  const paddingToBottom = 20
  return layoutMeasurement.height + contentOffset.y >= contentSize.height - paddingToBottom
}

// Typed messages (e.g. the Permit2 EIP-712 payload of a gasless LibertySwap) can
// carry BigInt values, which `JSON.stringify` cannot serialize and would otherwise
// throw on, crashing the whole sign screen into a black popup. Coerce BigInts to
// their decimal string so serialization is safe.
const bigIntReplacer = (_key: string, value: any) =>
  typeof value === 'bigint' ? value.toString() : value

const FallbackVisualization: FC<{
  messageToSign: ISignMessageController['messageToSign']
  setHasReachedBottom: (hasReachedBottom: boolean) => void
  hasReachedBottom: boolean
  responsiveSizeMultiplier?: number
}> = ({ messageToSign, setHasReachedBottom, hasReachedBottom, responsiveSizeMultiplier = 1 }) => {
  const { t } = useTranslation()
  const { styles } = useTheme(getStyles)
  const { maxWidthSize } = useWindowSize()
  const [containerHeight, setContainerHeight] = useState(0)
  const [contentHeight, setContentHeight] = useState(0)
  const [showRawTypedMessage, setShowRawTypedMessage] = useState(false)
  const statesForMultistateButton = useMemo(
    () => [
      { text: i18nKey('Parsed'), callback: () => setShowRawTypedMessage(false) },
      { text: i18nKey('Raw'), callback: () => setShowRawTypedMessage(true) }
    ],
    []
  )
  useEffect(() => {
    if (!messageToSign || !containerHeight || !contentHeight) return
    const isScrollNotVisible = contentHeight <= containerHeight
    if (setHasReachedBottom && !hasReachedBottom) setHasReachedBottom(isScrollNotVisible)
  }, [
    contentHeight,
    containerHeight,
    setHasReachedBottom,
    messageToSign,
    showRawTypedMessage,
    hasReachedBottom
  ])
  if (!messageToSign) return null

  const { content } = messageToSign

  return (
    <View style={[styles.container]}>
      <ScrollView
        onScroll={(e) => {
          if (isCloseToBottom(e.nativeEvent) && setHasReachedBottom) setHasReachedBottom(true)
        }}
        onLayout={(e) => {
          setContainerHeight(e.nativeEvent.layout.height)
        }}
        onContentSizeChange={(_, height) => {
          setContentHeight(height)
        }}
        scrollEventThrottle={400}
      >
        <Text
          selectable
          weight="regular"
          fontSize={(maxWidthSize('xl') ? 14 : 12) * responsiveSizeMultiplier}
          appearance="secondaryText"
          style={spacings.mb}
        >
          {content.kind === 'typedMessage' &&
            showRawTypedMessage &&
            JSON.stringify(content, bigIntReplacer, 4)}
          {content.kind === 'typedMessage' &&
            !showRawTypedMessage &&
            simplifyTypedMessage(content.message).map((i, index: number) => {
              // A leaf value may be a BigInt (e.g. amounts/deadlines in a Permit2
              // typed message); normalize to string before any string/JSON usage.
              const value = typeof i.value === 'bigint' ? i.value.toString() : i.value
              let componentToReturn = value

              const isProbablyADateWIthinRange =
                parseInt(value, 10) * 1000 > new Date('01/01/2000').getTime() &&
                parseInt(value, 10) * 1000 < new Date('01/01/2100').getTime()
              const isInfiniteAmount = parseInt(value, 10)?.toString(16) === '1'.padEnd(65, '0')

              if (isValidAddress(value))
                componentToReturn = (
                  <HumanizerAddress
                    chainId={messageToSign.chainId}
                    address={value}
                    fontSize={14 * responsiveSizeMultiplier}
                  />
                )
              else if (isProbablyADateWIthinRange)
                componentToReturn = new Date(parseInt(value, 10) * 1000).toUTCString()
              else if (isInfiniteAmount)
                componentToReturn = (
                  <View style={[flexbox.directionRow, flexbox.alignCenter]}>
                    <Text
                      fontSize={16 * responsiveSizeMultiplier}
                      weight="semiBold"
                      style={[spacings.mrTy]}
                    >
                      {t('Infinite amount')}
                    </Text>
                    <WarningFilledIcon
                      width={16 * responsiveSizeMultiplier}
                      height={16 * responsiveSizeMultiplier}
                    />
                  </View>
                )
              return (
                <div
                  style={{
                    ...(index < 2 ? { maxWidth: '75%' } : {}),
                    ...(i.isArrayItem && isHexString(value) ? { marginBottom: '0.5rem' } : {})
                  }}
                  key={JSON.stringify(i, bigIntReplacer)}
                >
                  <Text
                    style={[
                      i.type === 'key' && { fontWeight: 'bold' },
                      { marginLeft: i.n * 20 * responsiveSizeMultiplier }
                    ]}
                    fontSize={16 * responsiveSizeMultiplier}
                  >
                    {componentToReturn}
                  </Text>
                </div>
              )
            })}
          {content.kind === 'authorization-7702' && getMessageAsText(content.message as Hex)}
          {(content.kind === 'message' || content.kind === 'siwe') &&
            (getMessageAsText(content.message as Hex) || t('(Empty message)'))}
        </Text>
      </ScrollView>
      {content.kind === 'typedMessage' && (
        <MultistateToggleButton style={styles.toggleButton} states={statesForMultistateButton} />
      )}
    </View>
  )
}

export default memo(FallbackVisualization)
